/**
 * Admin Email Settings Page — unified settings console
 * General (Mail-Away sender) · Tax Collector Email · eFax Gateway
 */
import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Input } from '../components/ui/input';
import { Switch } from '../components/ui/switch';
import { Mail, RefreshCw, AlertCircle, Phone, Send } from 'lucide-react';
import TaxCollectorEmailSettingsCard from '../components/admin/TaxCollectorEmailSettingsCard';
import { AutomationToggle, FieldLabel, PanelHeader, SettingsSection, StatusDot, StickyActionBar } from '../components/admin/email/EmailSettingsPrimitives';

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';
const DEFAULT_SETTINGS = { from_email: '', from_name: '', reply_to: '', enabled: true, provider: 'aws_ses' };
const DEFAULT_EFAX = { enabled: false, gateway_domain: '' };

const WarningBanner = ({ title, text, testId }) => (
  <div className="mx-6 mt-4 flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5" data-testid={testId}>
    <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-600" />
    <div className="text-xs"><p className="font-medium text-amber-800">{title}</p><p className="text-amber-700">{text}</p></div>
  </div>
);

const AdminEmailSettings = () => {
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [savedSettings, setSavedSettings] = useState(DEFAULT_SETTINGS);
  const [efaxSettings, setEfaxSettings] = useState(DEFAULT_EFAX);
  const [savedEfax, setSavedEfax] = useState(DEFAULT_EFAX);
  const [taxEnabled, setTaxEnabled] = useState(null);
  const [loading, setLoading] = useState(true);
  const [section, setSection] = useState(() => new URLSearchParams(window.location.search).get('section') || 'general');
  const [saving, setSaving] = useState(false);
  const [savingEfax, setSavingEfax] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [updatedBy, setUpdatedBy] = useState(null);
  const [efaxLastUpdated, setEfaxLastUpdated] = useState(null);
  const [efaxUpdatedBy, setEfaxUpdatedBy] = useState(null);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    fetchSettings();
    fetchEfaxSettings();
    fetch(`${API}/admin/settings/tax-collector-email`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } })
      .then((r) => (r.ok ? r.json() : null)).then((d) => d && setTaxEnabled(!!d.enabled)).catch(() => {});
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/admin/settings/email`, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      if (!response.ok) throw new Error('Failed to fetch email settings');
      const data = await response.json();
      const next = {
        from_email: data.from_email || '',
        from_name: data.from_name || '',
        reply_to: data.reply_to || '',
        enabled: data.enabled !== false,
        provider: data.provider || 'aws_ses'
      };
      setSettings(next);
      setSavedSettings(next);
      setLastUpdated(data.updated_at);
      setUpdatedBy(data.updated_by);
    } catch (error) {
      console.error('Error fetching email settings:', error);
      toast.error('Failed to load email settings');
    } finally {
      setLoading(false);
    }
  };

  const fetchEfaxSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/admin/settings/efax`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (response.ok) {
        const data = await response.json();
        const next = { enabled: data.enabled || false, gateway_domain: data.gateway_domain || '' };
        setEfaxSettings(next);
        setSavedEfax(next);
        setEfaxLastUpdated(data.updated_at);
        setEfaxUpdatedBy(data.updated_by);
      }
    } catch (error) {
      console.error('Error fetching eFax settings:', error);
    }
  };

  const handleSaveEfax = async () => {
    if (efaxSettings.enabled && !efaxSettings.gateway_domain.trim()) {
      toast.error('Gateway domain is required when eFax is enabled');
      return;
    }
    try {
      setSavingEfax(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/admin/settings/efax`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled: efaxSettings.enabled, gateway_domain: efaxSettings.gateway_domain.trim() })
      });
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Failed to save eFax settings');
      }
      const data = await response.json();
      const next = { enabled: efaxSettings.enabled, gateway_domain: efaxSettings.gateway_domain.trim() };
      setEfaxSettings(next);
      setSavedEfax(next);
      setEfaxLastUpdated(data.updated_at);
      setEfaxUpdatedBy(data.updated_by);
      toast.success('eFax gateway settings saved');
    } catch (error) {
      toast.error(error.message || 'Failed to save eFax settings');
    } finally {
      setSavingEfax(false);
    }
  };

  const validateEmail = (email) => /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(email);

  const validateForm = () => {
    const newErrors = {};
    if (!settings.from_email.trim()) newErrors.from_email = 'Sender email is required';
    else if (!validateEmail(settings.from_email)) newErrors.from_email = 'Invalid email format';
    if (!settings.from_name.trim()) newErrors.from_name = 'Sender name is required';
    if (settings.reply_to && !validateEmail(settings.reply_to)) newErrors.reply_to = 'Invalid email format';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      toast.error('Please fix the validation errors');
      return;
    }
    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/admin/settings/email`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          from_email: settings.from_email.trim(),
          from_name: settings.from_name.trim(),
          reply_to: settings.reply_to?.trim() || null,
          enabled: settings.enabled
        })
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to save settings');
      }
      const data = await response.json();
      setSavedSettings(settings);
      setLastUpdated(data.updated_at);
      setUpdatedBy(data.updated_by);
      toast.success('Email settings saved successfully');
    } catch (error) {
      console.error('Error saving email settings:', error);
      toast.error(error.message || 'Failed to save email settings');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: null }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <RefreshCw className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  const ACTIVE = { text: 'Active', tone: 'emerald' };
  const OFF = { text: 'Off', tone: 'slate' };
  const SECTIONS = [
    { id: 'general', label: 'General', Icon: Mail, badge: settings.enabled ? ACTIVE : OFF },
    { id: 'tax', label: 'Tax Collector Email', Icon: Send, badge: taxEnabled === null ? null : taxEnabled ? ACTIVE : OFF },
    { id: 'efax', label: 'eFax Gateway', Icon: Phone,
      badge: efaxSettings.enabled ? (efaxSettings.gateway_domain ? ACTIVE : { text: 'Configuration Required', tone: 'amber' }) : OFF },
  ];
  const current = SECTIONS.find(s => s.id === section);
  const generalDirty = JSON.stringify(settings) !== JSON.stringify(savedSettings);
  const efaxDirty = JSON.stringify(efaxSettings) !== JSON.stringify(savedEfax);
  const efaxDomain = efaxSettings.gateway_domain || 'efaxsend.com';

  return (
    <div className="w-full max-w-[1480px] px-6 py-6 lg:px-8" data-testid="email-settings-page">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Email Settings</h1>
          <p className="mt-0.5 text-sm text-slate-500">Sender identities, automated requests and fax delivery.</p>
        </div>
        <div className="inline-flex items-center gap-2.5 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs shadow-sm" data-testid="ses-status-chip">
          <Mail className="h-3.5 w-3.5 text-blue-600" />
          <span className="font-medium text-slate-800">Amazon SES</span>
          <span className="h-3 w-px bg-slate-200" />
          <span className="inline-flex items-center gap-1.5 text-emerald-700"><span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />Production Ready</span>
        </div>
      </div>

      <div className="mt-5 grid grid-cols-1 overflow-clip rounded-2xl border border-slate-200 bg-white shadow-sm xl:grid-cols-[264px_minmax(0,1fr)]">
        <nav className="border-b border-slate-200 bg-slate-50/80 p-3 xl:border-b-0 xl:border-r" data-testid="email-settings-nav">
          <div className="xl:sticky xl:top-4">
            <div className="px-3 pb-2 pt-1 text-[10px] font-semibold uppercase tracking-wider text-slate-400">Configuration</div>
            <div className="flex flex-row flex-wrap gap-1 xl:flex-col">
              {SECTIONS.map(({ id, label, Icon, badge }) => {
                const active = section === id;
                return (
                  <button key={id} type="button" onClick={() => setSection(id)} data-testid={`email-settings-nav-${id}`} aria-current={active ? 'page' : undefined}
                    className={`group flex flex-1 items-center gap-2.5 rounded-lg px-3 py-2.5 text-left text-[13px] transition-[background-color,box-shadow,color] duration-150 xl:w-full xl:flex-none ${active ? 'bg-white font-semibold text-slate-900 shadow-sm ring-1 ring-slate-200' : 'font-medium text-slate-600 hover:bg-white/70 hover:text-slate-900'}`}>
                    <Icon className={`h-4 w-4 shrink-0 ${active ? 'text-blue-600' : 'text-slate-400 group-hover:text-slate-500'}`} />
                    <span className="flex-1 truncate">{label}</span>
                    {badge && <StatusDot tone={badge.tone}>{badge.text}</StatusDot>}
                  </button>
                );
              })}
            </div>
          </div>
        </nav>

        <div className="min-w-0 flex flex-col">
          {section === 'general' && (
            <div className="flex min-h-full flex-col" data-testid="email-settings-general-panel">
              <PanelHeader icon={Mail} iconCls="bg-blue-50 text-blue-600" title="General" subtitle="Mail-Away sender identity and notification emails"
                right={<>
                  <StatusDot tone={current.badge.tone}>{current.badge.text}</StatusDot>
                  <AutomationToggle label="Notifications" checked={settings.enabled}>
                    <Switch data-testid="email-enabled-toggle" checked={settings.enabled} onCheckedChange={(checked) => handleChange('enabled', checked)} />
                  </AutomationToggle>
                </>} />
              {!settings.enabled && (
                <WarningBanner testId="email-disabled-banner" title="Email notifications are disabled"
                  text="Mail-Away emails will not be sent automatically. The MLC Letter PDF is still generated and attached to the order." />
              )}
              <div className="flex-1 divide-y divide-slate-100">
                <SettingsSection title="Sender" tip="The sender must be a verified identity in Amazon SES. Contact support to add new sender addresses." testId="email-general-section-sender">
                  <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                    <div className="space-y-1.5">
                      <FieldLabel required htmlFor="from_name">Sender Display Name</FieldLabel>
                      <Input id="from_name" type="text" value={settings.from_name} onChange={(e) => handleChange('from_name', e.target.value)} placeholder="TitleFlow" className={errors.from_name ? 'border-red-500' : ''} />
                      {errors.from_name && <p className="text-xs text-red-500" data-testid="from-name-error">{errors.from_name}</p>}
                    </div>
                    <div className="space-y-1.5">
                      <FieldLabel required htmlFor="from_email">Sender Email Address</FieldLabel>
                      <Input id="from_email" type="email" value={settings.from_email} onChange={(e) => handleChange('from_email', e.target.value)} placeholder="noreply@yourcompany.com" className={errors.from_email ? 'border-red-500' : ''} />
                      {errors.from_email && <p className="text-xs text-red-500" data-testid="from-email-error">{errors.from_email}</p>}
                    </div>
                  </div>
                </SettingsSection>
                <SettingsSection title="Responses" tip="If set, replies are directed to this address instead of the sender email." testId="email-general-section-responses">
                  <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                    <div className="space-y-1.5">
                      <FieldLabel optional htmlFor="reply_to">Reply-To Email</FieldLabel>
                      <Input id="reply_to" type="email" value={settings.reply_to} onChange={(e) => handleChange('reply_to', e.target.value)} placeholder="support@yourcompany.com" className={errors.reply_to ? 'border-red-500' : ''} />
                      {errors.reply_to && <p className="text-xs text-red-500" data-testid="reply-to-error">{errors.reply_to}</p>}
                    </div>
                  </div>
                </SettingsSection>
              </div>
              <StickyActionBar updatedAt={lastUpdated} updatedBy={updatedBy} saving={saving} dirty={generalDirty}
                onCancel={() => { setSettings(savedSettings); setErrors({}); }} onSave={handleSave}
                saveTestId="save-email-settings-btn" cancelTestId="cancel-email-settings-btn" barTestId="email-general-save-bar" />
            </div>
          )}

          {section === 'tax' && <TaxCollectorEmailSettingsCard onStateChange={setTaxEnabled} />}

          {section === 'efax' && (
            <div className="flex min-h-full flex-col" data-testid="email-settings-efax-panel">
              <PanelHeader icon={Phone} iconCls="bg-emerald-50 text-emerald-600" title="eFax Gateway" subtitle="Email-to-fax delivery to tax offices"
                right={<>
                  <StatusDot tone={current.badge.tone}>{current.badge.text}</StatusDot>
                  <AutomationToggle checked={efaxSettings.enabled}>
                    <Switch data-testid="efax-enabled-toggle" checked={efaxSettings.enabled} onCheckedChange={(checked) => setEfaxSettings(prev => ({ ...prev, enabled: checked }))} />
                  </AutomationToggle>
                </>} />
              {!efaxSettings.enabled && (
                <WarningBanner testId="efax-disabled-banner" title="eFax is disabled"
                  text='Fax requests will not be sent when processors select "Fax Sent" + Pending. The fax request PDF is still generated and attached to the order.' />
              )}
              <div className="flex-1 divide-y divide-slate-100">
                <SettingsSection title="Gateway" tip='How it works: the processor clicks Pending + "Fax Sent"; the system reads the jurisdiction tax collector fax number, builds the destination {digits}@gateway and sends the Fax Request Letter PDF through eFax.' testId="efax-section-gateway">
                  <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                    <div className="space-y-1.5">
                      <FieldLabel required={efaxSettings.enabled} htmlFor="efax_gateway_domain">Gateway Domain</FieldLabel>
                      <Input id="efax_gateway_domain" data-testid="efax-gateway-domain-input" type="text" value={efaxSettings.gateway_domain}
                        onChange={(e) => setEfaxSettings(prev => ({ ...prev, gateway_domain: e.target.value }))} placeholder="e.g. efaxsend.com" />
                    </div>
                    <div className="space-y-1.5">
                      <FieldLabel>Fax destination</FieldLabel>
                      <div className="flex h-10 items-center rounded-md border border-dashed border-slate-200 bg-slate-50 px-3 font-mono text-xs text-slate-600" data-testid="efax-destination-preview">
                        {'{fax_number}'}@{efaxDomain}
                      </div>
                    </div>
                  </div>
                </SettingsSection>
              </div>
              <StickyActionBar updatedAt={efaxLastUpdated} updatedBy={efaxUpdatedBy} saving={savingEfax} dirty={efaxDirty}
                onCancel={() => setEfaxSettings(savedEfax)} onSave={handleSaveEfax}
                saveTestId="save-efax-settings-btn" cancelTestId="cancel-efax-settings-btn" barTestId="efax-save-bar" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminEmailSettings;
